import type { GridApiPremium, GridColDef } from '@mui/x-data-grid-premium';
import type { RefObject } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { COLUMN_LAYOUT } from '../columnVisibilitySelectorConstants';
import { isAutosizableColumn } from './useAutosizeColumns';

export type ColumnSizingPreference = {
    /** When true, grid autosizes. When false, saved manual widths are used. Default true. */
    autosize: boolean;
    widths: Record<string, number>;
};

type UsePreserveColumnConfigurationParams = {
    apiRef: RefObject<GridApiPremium>;
    persistAutosize: boolean;
    persistColumnWidths: boolean;
    gridId: string;
    preferencesLoaded: boolean;
    getPreference: (view: string, key: string, agent?: string) => unknown;
    savePreference: (view: string, key: string, value: string, agent?: string) => Promise<unknown>;
};

const AUTOSIZE_SIZING: ColumnSizingPreference = { autosize: true, widths: {} };

function parsePreference(value: unknown): ColumnSizingPreference {
    if (
        !value ||
        typeof value !== 'object' ||
        !('autosize' in value) ||
        value.autosize !== false ||
        !('widths' in value) ||
        !value.widths ||
        typeof value.widths !== 'object' ||
        Array.isArray(value.widths)
    ) {
        return AUTOSIZE_SIZING;
    }

    const widths: Record<string, number> = {};
    for (const [field, width] of Object.entries(value.widths)) {
        if (typeof width !== 'number' || !Number.isFinite(width) || width <= 0) {
            return AUTOSIZE_SIZING;
        }
        widths[field] = width;
    }

    return { autosize: false, widths };
}

function snapshotWidths(apiRef: RefObject<GridApiPremium>): Record<string, number> {
    const widths: Record<string, number> = {};
    apiRef.current.getAllColumns().forEach((column) => {
        if (isAutosizableColumn(column) && column.computedWidth > 0) {
            widths[column.field] = column.computedWidth;
        }
    });
    return widths;
}

/**
 * Pins saved widths on the column defs. MUI resets every width to the column def whenever a
 * new `columns` reference arrives (e.g. a parent re-render after saving preferences), so the
 * defs themselves must carry the manual widths.
 */
export function applyManualColumnWidths(columns: GridColDef[], widths: Record<string, number> | undefined) {
    if (!widths) {
        return columns;
    }

    return columns.map((column) => {
        const width = widths[column.field];
        if (width === undefined || !isAutosizableColumn(column)) {
            return column;
        }

        const nextColumn: GridColDef = { ...column, width };
        delete nextColumn.flex;
        return nextColumn;
    });
}

/**
 * Personal column-layout preference.
 * `persistAutosize` stores the Autosize on/off choice.
 * `persistColumnWidths` stores widths after a manual resize.
 * Prefs are keyed by DataGrid `id` only, so the same grid shares them everywhere it is used.
 */
export function usePreserveColumnConfiguration({
    apiRef,
    persistAutosize,
    persistColumnWidths,
    gridId,
    preferencesLoaded,
    getPreference,
    savePreference
}: UsePreserveColumnConfigurationParams) {
    const enabled = persistAutosize || persistColumnWidths;
    const canRestore = enabled && preferencesLoaded;

    const readSavedSizing = useCallback(
        () => parsePreference(getPreference(gridId, COLUMN_LAYOUT)),
        [getPreference, gridId]
    );

    const [sizing, setSizing] = useState<ColumnSizingPreference>(() =>
        canRestore ? readSavedSizing() : AUTOSIZE_SIZING
    );
    const sizingRef = useRef(sizing);
    const hasRestored = useRef(canRestore);

    const updateSizing = useCallback(
        async (next: ColumnSizingPreference) => {
            sizingRef.current = next;
            setSizing(next);
            try {
                await savePreference(gridId, COLUMN_LAYOUT, JSON.stringify(next));
            } catch (error) {
                console.error('Failed to save data grid column sizing', error);
            }
        },
        [gridId, savePreference]
    );

    const setAutosizeEnabled = useCallback(
        (nextAutosize: boolean) => {
            if (!persistAutosize) {
                return;
            }

            void updateSizing(
                nextAutosize
                    ? AUTOSIZE_SIZING
                    : { autosize: false, widths: persistColumnWidths ? snapshotWidths(apiRef) : {} }
            );
        },
        [apiRef, persistAutosize, persistColumnWidths, updateSizing]
    );

    useEffect(() => {
        if (!canRestore || hasRestored.current) {
            return;
        }

        hasRestored.current = true;
        const saved = readSavedSizing();
        sizingRef.current = saved;
        setSizing(saved);
    }, [canRestore, readSavedSizing]);

    useEffect(() => {
        if (!persistColumnWidths) {
            return;
        }

        function handleColumnResizeStop() {
            // Keep widths of columns that are currently hidden; overwrite the rest with what is on screen.
            const widths = { ...sizingRef.current.widths, ...snapshotWidths(apiRef) };
            void updateSizing({ autosize: false, widths });
        }

        return apiRef.current.subscribeEvent('columnResizeStop', handleColumnResizeStop);
    }, [apiRef, persistColumnWidths, updateSizing]);

    const autosizeEnabled = !persistAutosize || sizing.autosize;
    const manualWidths =
        persistColumnWidths && !sizing.autosize && Object.keys(sizing.widths).length > 0 ? sizing.widths : undefined;

    return {
        autosizeEnabled: manualWidths ? false : autosizeEnabled,
        manualWidths,
        setAutosizeEnabled
    };
}
